import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Bell, Check, CheckCheck, Clock, FileText, Users, Building2, Calendar, AlertCircle, X } from 'lucide-react';
import { formatRelativeTime, formatDate } from '../lib/dateUtils';
import PushPrefToggle from './push/PushPrefToggle';

// Notification type configurations
const notificationConfig = {
  task_assigned: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
  task_updated: { icon: FileText, color: 'text-green-500', bg: 'bg-green-50' },
  task_completed: { icon: Check, color: 'text-green-500', bg: 'bg-green-50' },
  leave_approved: { icon: Calendar, color: 'text-green-500', bg: 'bg-green-50' },
  leave_rejected: { icon: Calendar, color: 'text-red-500', bg: 'bg-red-50' },
  leave_request: { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-50' },
  new_employee: { icon: Users, color: 'text-purple-500', bg: 'bg-purple-50' },
  new_client: { icon: Building2, color: 'text-pink-500', bg: 'bg-pink-50' },
  announcement: { icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-50' },
  default: { icon: Bell, color: 'text-neutral-500', bg: 'bg-white/40' }
};

function formatTimeAgo(timestamp) {
  // Centralized relative-time rendering
  return formatRelativeTime(timestamp);
}

// Bucket notifications into Today / Yesterday / Earlier This Week / Earlier
function bucketByDay(notifications) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const sevenDaysAgo = new Date(startOfToday);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const buckets = { today: [], yesterday: [], week: [], earlier: [] };
  for (const n of notifications) {
    if (!n.created_at) { buckets.earlier.push(n); continue; }
    const d = new Date(n.created_at);
    if (isNaN(d.getTime())) { buckets.earlier.push(n); continue; }
    if (d >= startOfToday) buckets.today.push(n);
    else if (d >= startOfYesterday) buckets.yesterday.push(n);
    else if (d >= sevenDaysAgo) buckets.week.push(n);
    else buckets.earlier.push(n);
  }
  return buckets;
}

export default function NotificationBell({ api, portalType = 'admin', accentColor = 'pink' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const colorClasses = {
    pink: { badge: 'bg-pink-500', button: 'hover:bg-pink-50', ring: 'ring-pink-500' },
    purple: { badge: 'bg-purple-500', button: 'hover:bg-purple-50', ring: 'ring-purple-500' },
    blue: { badge: 'bg-blue-500', button: 'hover:bg-blue-50', ring: 'ring-blue-500' }
  };

  const colors = colorClasses[accentColor] || colorClasses.pink;

  // Bucket notifications by day for grouped rendering (Today / Yesterday / This Week / Earlier)
  const buckets = useMemo(() => bucketByDay(notifications), [notifications]);

  // Determine API endpoint based on portal type
  const getEndpoint = (path) => {
    const baseEndpoints = {
      admin: '/api/admin/notifications',
      employee: '/api/employee/notifications',
      client: '/api/client/notifications'
    };
    return `${baseEndpoints[portalType]}${path}`;
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get(getEndpoint(''));
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(getEndpoint(`/${notificationId}/read`));
      setNotifications(prev => 
        prev.map(n => n._id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put(getEndpoint('/mark-all-read'));
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Fetch notifications on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [portalType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationConfig = (type) => {
    return notificationConfig[type] || notificationConfig.default;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-xl transition-colors ${colors.button} ${isOpen ? colors.ring + ' ring-2' : ''}`}
        data-testid="notification-bell"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="w-6 h-6 text-neutral-600" />
        {unreadCount > 0 && (
          <span className={`absolute -top-1 -right-1 w-5 h-5 ${colors.badge} text-white text-xs font-bold rounded-full flex items-center justify-center`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-sm bg-white rounded-2xl border border-neutral-200 shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between bg-white">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-[#1a1a2e]">Notifications</h3>
              {unreadCount > 0 && (
                <span className={`px-2 py-0.5 ${colors.badge} text-white text-xs font-medium rounded-full`}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto bg-white">
            {loading && notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="w-6 h-6 border-2 border-neutral-200 border-t-neutral-500 rounded-full animate-spin mx-auto"></div>
                <p className="text-sm text-neutral-500 mt-2">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-neutral-500 font-medium">No notifications yet</p>
                <p className="text-sm text-neutral-400 mt-1">You&apos;re all caught up!</p>
              </div>
            ) : (
              <>
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'week', label: 'Earlier this week' },
                  { key: 'earlier', label: 'Earlier' },
                ].map(({ key, label }) => {
                  const items = buckets[key];
                  if (!items.length) return null;
                  return (
                    <div key={key}>
                      <div className="sticky top-0 z-10 px-4 py-1.5 bg-neutral-50 border-b border-neutral-200 text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
                        {label}
                      </div>
                      {items.map((notification) => {
                        const config = getNotificationConfig(notification.type);
                        const IconComponent = config.icon;
                        return (
                          <div
                            key={notification._id}
                            className={`px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors cursor-pointer ${!notification.is_read ? 'bg-blue-50/60' : 'bg-white'}`}
                            onClick={() => !notification.is_read && markAsRead(notification._id)}
                            data-testid={`notification-${notification._id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                                <IconComponent className={`w-5 h-5 ${config.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className={`text-sm ${!notification.is_read ? 'font-semibold text-[#1a1a2e]' : 'text-neutral-700'}`}>
                                    {notification.title}
                                  </p>
                                  {!notification.is_read && (
                                    <span className={`w-2 h-2 rounded-full ${colors.badge} flex-shrink-0 mt-1.5`}></span>
                                  )}
                                </div>
                                <p className="text-sm text-neutral-500 mt-0.5 line-clamp-2">{notification.message}</p>
                                <div className="flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3 text-neutral-400" />
                                  <span className="text-xs text-neutral-400">{formatTimeAgo(notification.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <PushPrefToggle api={api} />
          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between">
              <span className="text-[11px] text-neutral-500">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm text-neutral-500 hover:text-neutral-700 py-1"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
